/**
 * FindingDetailsPage - Phase 6: FINDING / EVIDENCE INVESTIGATION WORKSPACE
 * 
 * Production-grade investigation workspace for engineers to understand WHY a finding
 * exists, WHERE it is, WHAT evidence supports it, and WHAT remediation is available.
 * 
 * Architecture:
 * - Evidence-first main content area (visually primary)
 * - Contextual sidebar with project/audit/rule/related findings
 * - Progressive disclosure for long sections
 * - Security: respects data lifecycle/destroyed evidence boundaries
 * - Responsive: desktop 1440x900 primary, 1366x768 usable, stacks on narrow
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { api, FindingDetailsResponse, RelatedFindingsResponse } from '../lib/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { SeverityBadge } from '../components/SeverityBadge'
import { StatusBadge } from '../components/StatusBadge'
import { Card } from '../components/design-system/Card'
import { CodeBlock } from '../components/design-system/CodeBlock'
import { KnowledgeTag } from '../components/design-system/KnowledgeTag'
import { Button } from '../components/design-system/Button'
import { EmptyState } from '../components/design-system/EmptyState'
import { FixWithAIButton } from '../components/FixWithAI'
import { FindingReviewCard } from '../components/FindingReview'
import { useLocale } from '../contexts/LocaleContext'
import {
  categoryLabel,
  confidenceLabel,
  evidenceTypeLabel,
  formatDateTime,
  statusLabel,
  tagLabel,
  trackLabel,
  type Locale,
} from '../lib/i18n'
import { localizeFindingText } from '../lib/findingLocale'

export function FindingDetailsPage() {
  const { findingId } = useParams<{ findingId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, locale } = useLocale()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [finding, setFinding] = useState<FindingDetailsResponse | null>(null)
  const [relatedFindings, setRelatedFindings] = useState<RelatedFindingsResponse | null>(null)
  const [loadingRelated, setLoadingRelated] = useState(false)
  
  // Preserve filter state from location state (if navigating from findings table)
  const returnPath = (location.state as { from?: string })?.from || '/findings'
  
  useEffect(() => {
    if (findingId) {
      loadFinding()
    }
  }, [findingId])
  
  async function loadFinding() {
    if (!findingId) return
    
    try {
      setLoading(true)
      setError(null)
      const data = await api.getFindingDetails(findingId)
      setFinding(data)
      
      // Load related findings in parallel
      loadRelatedFindings(findingId)
    } catch (err) {
      console.error('Failed to load finding:', err)
      setError(err instanceof Error ? err.message : 'Failed to load finding')
    } finally {
      setLoading(false)
    }
  }
  
  async function loadRelatedFindings(id: string) {
    try {
      setLoadingRelated(true)
      const related = await api.getRelatedFindings(id, 5)
      setRelatedFindings(related)
    } catch (err) {
      console.error('Failed to load related findings:', err)
      // Non-blocking error - don't set main error state
    } finally {
      setLoadingRelated(false)
    }
  }
  
  function handleBack() {
    navigate(returnPath, { replace: true })
  }
  
  if (loading) {
    return <LoadingSpinner message={t("finding.loading")} />
  }
  
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card p-6 border-2 border-[var(--error)]">
          <h2 className="text-lg font-medium mb-2 text-[var(--error)]">{t("finding.error")}</h2>
          <p className="text-sm mb-4">{error}</p>
          <Button onClick={handleBack} variant="secondary" size="sm">
            ← {t("common.back")}
          </Button>
        </div>
      </div>
    )
  }
  
  if (!finding) {
    return null
  }
  
  // Extract rule provenance from extra_metadata
  const ruleId = finding.extra_metadata?.rule_id as string | undefined
  const ruleVersion = finding.extra_metadata?.rule_version as string | undefined
  const ruleSource = finding.extra_metadata?.rule_source as string | undefined
  
  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header / Summary */}
      <header className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Button
            onClick={handleBack}
            variant="secondary"
            size="sm"
            aria-label={t("finding.backAria")}
          >
            {t("common.back")}
          </Button>
        </div>
        
        <div className="flex flex-col gap-3 mb-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-medium">{localizeFindingText(locale, finding.title)}</h1>
            <SeverityBadge severity={finding.severity} />
            <StatusBadge status={finding.status} />
          </div>
          
          <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)]">
            <span className="font-mono">ID: {finding.id.substring(0, 8)}</span>
            <span>•</span>
            <span>{trackLabel(locale, finding.track)}</span>
            <span>•</span>
            <span>{categoryLabel(locale, finding.category)}</span>
            <span>•</span>
            <span>{t("review.confidence")}: {confidenceLabel(locale, finding.confidence)}</span>
          </div>
          
          {finding.audit_context && (
            <div className="text-sm text-[var(--text-secondary)]">
              {t("finding.project")}: <span className="font-medium text-[var(--text-primary)]">{finding.audit_context.project_name}</span>
              {' • '}
              {t("finding.audit")}: <button
                onClick={() => navigate(`/audit/${finding.audit_run_id}`)}
                className="text-[var(--accent-primary)] hover:underline"
              >
                {finding.audit_run_id.substring(0, 8)}
              </button>
              {' • '}
              <span>{statusLabel(locale, finding.audit_context.status)}</span>
            </div>
          )}
        </div>
      </header>
      
      {/* Main workspace layout: evidence-first content + contextual sidebar */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content area: evidence and explanation (2 columns on large screens) */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Evidence Section - Visually Primary */}
          <EvidenceSection finding={finding} t={t} locale={locale} />
          
          {/* Description / Impact */}
          <Card>
            <FindingReviewCard finding={finding} />
          </Card>
          
          {/* Reproduction Steps */}
          {finding.reproduction_steps && finding.reproduction_steps.length > 0 && (
            <Card>
              <h2 className="text-lg font-medium mb-3">{t("review.reproduction")}</h2>
              <ol className="list-decimal list-inside space-y-2">
                {finding.reproduction_steps.map((step, i) => (
                  <li key={i} className="text-sm">{step}</li>
                ))}
              </ol>
              
              {finding.reproduction_script && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium mb-2 text-[var(--text-secondary)]">
                    {t('finding.script')}
                  </h3>
                  <CodeBlock
                    code={finding.reproduction_script}
                    language="bash"
                    lineNumbers={false}
                  />
                </div>
              )}
            </Card>
          )}
          
          <Card>
            <div className="flex items-center gap-4 text-sm">
              {finding.remediation_effort && (
                <div>
                  <span className="text-[var(--text-secondary)]">{t("review.effort")}: </span>
                  <span className="font-medium capitalize">{finding.remediation_effort}</span>
                </div>
              )}
              <FixWithAIButton
                finding={finding}
                onFixRunCreated={(fixRunId) => {
                  console.log('Fix run created:', fixRunId)
                }}
              />
            </div>
          </Card>
        </div>
        
        {/* Contextual Sidebar (1 column on large screens, stacks below on narrow) */}
        <aside className="space-y-4">
          
          {/* Metadata Card */}
          <Card>
            <h3 className="text-sm font-medium mb-3">{t("review.details")}</h3>
            <div className="space-y-3 text-sm">
              
              {finding.primary_location && (
                <div>
                  <div className="text-xs text-[var(--text-tertiary)] mb-1">{t("review.location")}</div>
                  <div className="font-mono text-xs break-all bg-[var(--bg-secondary)] p-2 rounded">
                    {finding.primary_location}
                    {finding.line_number && (
                      <span className="text-[var(--text-secondary)]">:{finding.line_number}</span>
                    )}
                  </div>
                </div>
              )}
              
              {(finding.cwe_ids.length > 0 || finding.cve_ids.length > 0) && (
                <div>
                  <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('finding.securityIds')}</div>
                  <div className="space-y-1">
                    {finding.cwe_ids.length > 0 && (
                      <div className="font-mono text-xs">CWE: {finding.cwe_ids.join(', ')}</div>
                    )}
                    {finding.cve_ids.length > 0 && (
                      <div className="font-mono text-xs">CVE: {finding.cve_ids.join(', ')}</div>
                    )}
                  </div>
                </div>
              )}
              
              {finding.tags.length > 0 && (
                <div>
                  <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('finding.tags')}</div>
                  <div className="flex flex-wrap gap-1">
                    {finding.tags.map(tag => (
                      <span key={tag} className="text-xs px-2 py-0.5 bg-[var(--bg-secondary)] rounded">
                        {tagLabel(locale, tag)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                  <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('finding.timestamps')}</div>
                <div className="space-y-1 text-xs">
                  <div>{t('finding.created')}: {formatDateTime(locale, finding.created_at)}</div>
                  <div>{t('finding.firstSeen')}: {formatDateTime(locale, finding.first_seen_at)}</div>
                  <div>{t('finding.lastSeen')}: {formatDateTime(locale, finding.last_seen_at)}</div>
                </div>
              </div>
            </div>
          </Card>
          
          {/* Rule / Knowledge Provenance */}
          {(ruleId || ruleSource) && (
            <Card>
              <h3 className="text-sm font-medium mb-3">{t('finding.ruleProvenance')}</h3>
              <div className="space-y-2 text-sm">
                {ruleId && (
                  <div>
                    <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('finding.ruleId')}</div>
                    <div className="flex items-center gap-2">
                      <KnowledgeTag knowledgeId={ruleId} />
                      {ruleVersion && (
                        <span className="text-xs text-[var(--text-secondary)]">v{ruleVersion}</span>
                      )}
                    </div>
                  </div>
                )}
                
                {ruleSource && (
                  <div>
                    <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('finding.source')}</div>
                    <div className="text-xs capitalize">{ruleSource}</div>
                  </div>
                )}
                
                {/* Deep link to Knowledge Base (Phase 10/11) */}
                <Button
                  onClick={async () => {
                    if (!ruleId) {
                      navigate('/knowledge-base')
                      return
                    }
                    try {
                      const entry = await api.getKBEntryByRuleId(ruleId, ruleVersion)
                      navigate(`/knowledge-base/${entry.id}`)
                    } catch {
                      navigate(`/knowledge-base?search=${encodeURIComponent(ruleId)}`)
                    }
                  }}
                  variant="secondary"
                  size="sm"
                  className="w-full mt-2"
                >
                  {t('finding.viewKb')}
                </Button>
              </div>
            </Card>
          )}
          
          {/* Audit Context Card */}
          {finding.audit_context && (
            <Card>
              <h3 className="text-sm font-medium mb-3">{t('finding.auditContext')}</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('finding.project')}</div>
                  <button
                    onClick={() => navigate(`/projects`)}
                    className="text-[var(--accent-primary)] hover:underline text-sm"
                  >
                    {finding.audit_context.project_name}
                  </button>
                </div>
                
                <div>
                  <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('finding.auditRun')}</div>
                  <button
                    onClick={() => navigate(`/audit/${finding.audit_run_id}`)}
                    className="font-mono text-xs text-[var(--accent-primary)] hover:underline"
                  >
                    {finding.audit_run_id.substring(0, 8)}
                  </button>
                  <div className="text-xs text-[var(--text-secondary)] mt-1">
                    {statusLabel(locale, finding.audit_context.status)}
                  </div>
                </div>
                
                {finding.audit_context.tracks_executed.length > 0 && (
                  <div>
                    <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('finding.tracks')}</div>
                    <div className="flex flex-wrap gap-1">
                      {finding.audit_context.tracks_executed.map(track => (
                        <span key={track} className="text-xs px-2 py-0.5 bg-[var(--bg-secondary)] rounded">
                          {trackLabel(locale, track)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {finding.audit_context.completed_at && (
                  <div className="text-xs text-[var(--text-secondary)]">
                    {t('finding.completed')}: {formatDateTime(locale, finding.audit_context.completed_at)}
                  </div>
                )}
              </div>
            </Card>
          )}
          
          {/* Related Findings */}
          {relatedFindings && relatedFindings.total > 0 && (
            <Card>
              <h3 className="text-sm font-medium mb-3">
                {t('finding.related')} ({relatedFindings.total})
              </h3>
              <div className="space-y-2">
                {relatedFindings.related.map(related => (
                  <button
                    key={related.id}
                    onClick={() => navigate(`/finding/${related.id}`)}
                    className="w-full text-left p-2 rounded hover:bg-[var(--bg-secondary)] transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <SeverityBadge severity={related.severity} size="sm" />
                      <span className="text-xs text-[var(--text-tertiary)]">
                        {t(`rel.${related.relation_type}`)}
                      </span>
                    </div>
                    <div className="text-sm line-clamp-2">{localizeFindingText(locale, related.title)}</div>
                    <div className="text-xs text-[var(--text-secondary)] mt-1">
                      {related.project_name}
                    </div>
                  </button>
                ))}
                
                {relatedFindings.total > 5 && (
                  <Button
                    onClick={() => navigate('/findings', { state: { 
                      filters: { 
                        search: finding.finding_fingerprint 
                      }
                    }})}
                    variant="secondary"
                    size="sm"
                    className="w-full"
                  >
                    {t('finding.viewAllRelated')}
                  </Button>
                )}
              </div>
            </Card>
          )}
          
          {loadingRelated && (
            <Card>
              <div className="text-sm text-[var(--text-secondary)]">
                {t('finding.loadingRelated')}
              </div>
            </Card>
          )}
        </aside>
      </div>
    </div>
  )
}

/**
 * Evidence Section Component
 * 
 * Renders evidence with security boundaries:
 * - Shows available evidence snippets with proper formatting
 * - Handles destroyed/redacted evidence per data lifecycle policy
 * - Prevents raw source/secret leakage
 * - Accessible code blocks with line numbers and highlighting
 */
function EvidenceSection({
  finding,
  t,
  locale,
}: {
  finding: FindingDetailsResponse
  t: (key: string) => string
  locale: Locale
}) {
  const normalizeEvidencePreview = (value: string | null | undefined): string => {
    const t0 = (value || '').trim()
    if (!t0) return ''
    // Caret-only placeholder (e.g. '^') is not an actual code fragment.
    if (/^\^+$/.test(t0)) return ''
    return t0
  }

  if (!finding.evidence || finding.evidence.length === 0) {
    return (
      <Card>
        <h2 className="text-lg font-medium mb-3">{t('finding.evidence')}</h2>
        <EmptyState
          icon="📄"
          title={t('finding.noEvidence')}
          description={t('finding.noEvidenceBody')}
        />
      </Card>
    )
  }
  
  return (
    <Card>
      <h2 className="text-lg font-medium mb-3">
        {t('finding.evidence')} ({finding.evidence.length})
      </h2>
      
      <div className="space-y-4">
        {finding.evidence.map((evidence, index) => (
          <div
            key={evidence.id}
            className="border border-[var(--reviewer-border)] bg-[var(--reviewer-surface)] p-4"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <span className="text-xs font-medium text-[var(--text-tertiary)]">
                {evidenceTypeLabel(locale, evidence.evidence_type)}
                {evidence.reconstructed ? (
                  <span className="ml-2 text-[var(--text-tertiary)]">
                    · {locale === 'ru' ? 'восстановлено из снимка' : 'reconstructed from snapshot'}
                  </span>
                ) : null}
              </span>
              {index === 0 && (
                <span className="text-xs px-2 py-0.5 bg-[var(--accent-primary)] text-white rounded">
                  {t('finding.primary')}
                </span>
              )}
            </div>

            {evidence.description && (
              <p className="text-sm mb-3 text-[var(--text-secondary)]">{evidence.description}</p>
            )}

            {(() => {
              const preview = normalizeEvidencePreview(evidence.content_preview)
              const hasRawPreview = Boolean(evidence.content_preview && evidence.content_preview.trim())

              if (preview) {
                const fileLine =
                  evidence.snippet_anchor_line ||
                  evidence.line_number ||
                  finding.line_number ||
                  null
                const startLineNumber =
                  evidence.snippet_start_line ||
                  (fileLine
                    ? Math.max(
                        1,
                        fileLine -
                          Math.min(4, fileLine - 1, Math.max(0, preview.split('\n').length - 1)),
                      )
                    : 1)
                return (
                  <CodeBlock
                    code={preview}
                    filename={evidence.description || finding.primary_location || undefined}
                    lineNumbers={true}
                    startLineNumber={startLineNumber}
                    highlightLines={fileLine ? [fileLine] : undefined}
                  />
                )
              }

              // If content_preview exists but is caret-only, don't show "stored/destroyed"
              // as that misrepresents the data lifecycle.
              if (!preview && hasRawPreview) return null

              if (evidence.storage_ref) {
                return (
                  <div className="text-xs text-[var(--text-tertiary)] bg-[var(--bg-secondary)] p-2 rounded">
                    {t('finding.stored')} (storage_ref: {evidence.storage_ref.substring(0, 16)}...)
                  </div>
                )
              }

              return (
                <div className="text-xs text-[var(--text-tertiary)] bg-[var(--bg-secondary)] p-2 rounded">
                  {t('finding.destroyed')}
                </div>
              )
            })()}
          </div>
        ))}
      </div>
    </Card>
  )
}
